import { Close } from '@mui/icons-material';
import { Box, Dialog, DialogContent, Divider, IconButton, Typography } from '@mui/material';
import {
  IPurchaseOrderRow,
  ITransportActiveDocumentLine,
  ITransportDocumentLine
} from 'interfaces';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  orderLineService,
  transportDocumentActiveLineService,
  transportDocumentLineService
} from 'services/api';

interface InfoTransportActiveDocumentLineDialogFormParams {
  open: boolean;
  row: {
    id: number;
    quantity: number;
    product: number;
    product_description: string;
    order_line: number;
  };
  onClose: () => void;
}

function InfoTransportActiveDocumentLineDialog(
  params: InfoTransportActiveDocumentLineDialogFormParams
) {
  const { t } = useTranslation();
  const translationPrefix = 'pages.transport-active-document-line.info';
  const { open, row, onClose } = params;

  const [orderLine, setOrderLine] = useState<IPurchaseOrderRow>(null);
  const [deliveries, setDeliveries] = useState<ITransportDocumentLine[]>([]);
  const [pendingDeliveries, setPendingDeliveries] = useState<ITransportActiveDocumentLine[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!row?.order_line) return;

    orderLineService.get(row.order_line).then(setOrderLine);
  }, [row?.order_line]);

  useEffect(() => {
    if (!open) return;
    if (!orderLine) return;

    transportDocumentActiveLineService
      .getAllBaseInformation({
        order_line: orderLine.id,
        product_code: orderLine.product.code
      })
      .then(setPendingDeliveries);

    transportDocumentLineService
      .getAllBaseInformation({
        order_code: orderLine.ref,
        product: orderLine.product.code
      })
      .then(setDeliveries);
    setIsLoading(true);
  }, [open, orderLine?.ref, orderLine?.id]);

  useEffect(() => {
    if (!open) {
      setOrderLine(null);
      setDeliveries([]);
      setPendingDeliveries([]);
      setIsLoading(false);
    }
  }, [open]);

  const remainingQuantity = orderLine?.remaining_quantity;
  const rowQuantity = row?.quantity;
  const pendingQuantity = Math.max(0, remainingQuantity - rowQuantity);

  return (
    <>
      {isLoading && (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          {!!orderLine && (
            <>
              <DialogContent>
                <IconButton
                  onClick={onClose}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: 'text.secondary'
                  }}>
                  <Close />
                </IconButton>

                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                  {t(`global.product`)}: {row.product_description}
                </Typography>

                <Divider sx={{ marginTop: 2, marginBottom: 1 }} />
                <Box sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>{t(`${translationPrefix}.remaining_quantity`)}</Typography>
                    <Typography fontWeight="bold">{remainingQuantity}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>{t(`${translationPrefix}.transfer_quantity`)}</Typography>
                    <Typography fontWeight="bold">{rowQuantity}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>{t(`${translationPrefix}.pending_delivery_quantity`)}</Typography>
                    <Typography
                      fontWeight="bold"
                      color={pendingQuantity > 0 ? 'success.main' : 'error.main'}>
                      {pendingQuantity}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography component="div" sx={{ whiteSpace: 'pre-line' }}>
                  <b>{'Consegne effettuate:'}</b>
                </Typography>
                <Box>
                  {deliveries.length > 0 ? (
                    deliveries.map((deliveries) => (
                      <Box key={deliveries.id} sx={{ p: 1 }}>
                        <Typography> {deliveries.document}</Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ my: 1 }}>
                      {t(`${translationPrefix}.zero_deliveries_completed`)}
                    </Typography>
                  )}
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography component="div" sx={{ whiteSpace: 'pre-line' }}>
                  <b>{'Consegne attese:'}</b>
                </Typography>
                <Box>
                  {pendingDeliveries.length > 0 ? (
                    pendingDeliveries.map((delivery) => (
                      <Box key={delivery.id} sx={{ p: 1 }}>
                        <Typography>
                          {t(`${translationPrefix}.transfer_document_ref`)} {delivery.document_ref}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ my: 1 }}>
                      {t(`${translationPrefix}.zero_pending_deliveries`)}
                    </Typography>
                  )}
                </Box>
              </DialogContent>
            </>
          )}
        </Dialog>
      )}
    </>
  );
}

export default InfoTransportActiveDocumentLineDialog;
